import React, { useState, useCallback } from 'react';
import { Upload, FileText, Plus, Loader2, AlertCircle, Zap, CheckCircle, FileCheck } from 'lucide-react';
import { CompareGrid } from './components/CompareGrid';
import { openaiClient, processPDFInvoice, useAsyncOperation } from './lib/api';
import { simulateTariff } from './lib/simulate';
import type { Invoice, Tariff, ComparisonColumn } from './lib/types';

// Helper function para ajustar precios según IVA
function adjustPriceForVAT(
  originalPrice: number, 
  originalIncludesVAT: boolean, 
  targetIncludesVAT: boolean
): number {
  // Si no hay cambio en el estado de IVA, devolver precio original
  if (originalIncludesVAT === targetIncludesVAT) {
    return originalPrice;
  }
  
  // Si original incluía IVA y queremos sin IVA
  if (originalIncludesVAT && !targetIncludesVAT) {
    return originalPrice / 1.21;
  }
  
  // Si original no incluía IVA y queremos con IVA
  if (!originalIncludesVAT && targetIncludesVAT) {
    return originalPrice * 1.21;
  }
  
  return originalPrice;
}

// Helper function para formatear precios en preview usando regla "precio más caro"
function formatPriceForPreview(tariff: Tariff, period: 'p1' | 'p2' | 'p3', type: 'energy' | 'power'): string {
  const isFlatRate = !tariff.energy.p2_eur_kwh && !tariff.energy.p3_eur_kwh;
  const unit = type === 'energy' ? '€/kWh' : '€/kW·día';
  
  let rate: number | null;
  let suffix = '';
  
  if (isFlatRate && type === 'energy') {
    // Tarifa fija de energía: usar siempre P1
    rate = tariff.energy.p1_eur_kwh;
    suffix = ' (fija)';
  } else if (type === 'power') {
    // Potencia: solo P1 y P2
    if (period === 'p3') {
      return 'No aplicable'; // P3 no existe en potencia
    }
    
    const fieldName = `${period}_eur_kw_day` as keyof typeof tariff.power;
    rate = tariff.power[fieldName] as number | null;
    
    // Si no existe, usar el más caro disponible de P1/P2
    if (rate === null || rate === undefined) {
      const availablePrices = [
        tariff.power.p1_eur_kw_day,
        tariff.power.p2_eur_kw_day
      ].filter(price => price !== null && price !== undefined) as number[];
      
      if (availablePrices.length > 0) {
        rate = Math.max(...availablePrices);
        suffix = ' (precio más caro)';
      } else {
        return 'No detectado';
      }
    }
  } else {
    // Energía: P1, P2, P3
    const fieldName = `${period}_eur_kwh` as keyof typeof tariff.energy;
    rate = tariff.energy[fieldName] as number | null;
    
    // Si no existe, usar el más caro disponible
    if (rate === null || rate === undefined) {
      const availablePrices = [
        tariff.energy.p1_eur_kwh,
        tariff.energy.p2_eur_kwh,
        tariff.energy.p3_eur_kwh
      ].filter(price => price !== null && price !== undefined) as number[];
      
      if (availablePrices.length > 0) {
        rate = Math.max(...availablePrices);
        suffix = ' (precio más caro)';
      } else {
        return 'No detectado';
      }
    }
  }
  
  if (rate === null || rate === undefined) return 'No detectado';
  
  return `${rate.toFixed(6)} ${unit}${suffix}`;
}

function App() {
  const [columns, setColumns] = useState<ComparisonColumn[]>([]);
  const [tariffText, setTariffText] = useState('');
  const [invoiceText, setInvoiceText] = useState('');
  const [parsedTariff, setParsedTariff] = useState<Tariff | null>(null);
  const [parsedTariffIncludesVAT, setParsedTariffIncludesVAT] = useState(true);
  const [showWithVAT, setShowWithVAT] = useState(true);
  
  const { loading: invoiceLoading, error: invoiceError, execute: executeInvoiceOp } = useAsyncOperation<Invoice>();
  const { loading: tariffLoading, error: tariffError, execute: executeTariffOp } = useAsyncOperation<Tariff>();

  // Manejar upload de PDF y procesarlo directamente
  const handleFileUpload = useCallback(async (file: File) => {
    console.log('📁 File uploaded:', file.name);
    
    // Procesar PDF completo con OpenAI
    const invoice = await executeInvoiceOp(() => processPDFInvoice(file));
    
    if (invoice) {
      // Remover factura existente si hay una
      const newColumns = columns.filter(col => col.type !== 'invoice');
      
      // Añadir nueva factura como primera columna
      setColumns([
        { type: 'invoice', data: invoice },
        ...newColumns
      ]);
      
      // Mostrar el texto extraído para referencia (opcional)
      setInvoiceText(`✅ PDF procesado: ${file.name}\n\nTotal: ${invoice.total_pdf}€\nPeríodo: ${invoice.period.start} a ${invoice.period.end}`);
    }
    
    // CRÍTICO: Resetear input para permitir subidas repetidas
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }, [columns, executeInvoiceOp]);

  // Parsear texto de factura manualmente pegado
  const handleParseInvoice = useCallback(async () => {
    if (!invoiceText.trim() || invoiceText.startsWith('✅ PDF procesado:')) return;

    console.log('📝 Parsing manual invoice text...');
    const invoice = await executeInvoiceOp(() => openaiClient.parseInvoice(invoiceText));
    
    if (invoice) {
      // Remover factura existente si hay una
      const newColumns = columns.filter(col => col.type !== 'invoice');
      
      // Añadir nueva factura como primera columna
      setColumns([
        { type: 'invoice', data: invoice },
        ...newColumns
      ]);
    }
  }, [invoiceText, columns, executeInvoiceOp]);

  // Parsear tarifa con OpenAI
  const handleParseTariff = useCallback(async () => {
    if (!tariffText.trim()) return;

    // Verificar que hay una factura base para comparar
    const hasInvoice = columns.some(col => col.type === 'invoice');
    if (!hasInvoice) {
      alert('⚠️ Primero debes subir una factura PDF para poder comparar tarifas');
      return;
    }

    console.log('📝 Parsing tariff text with OpenAI...');
    const tariff = await executeTariffOp(() => openaiClient.parseTariff(tariffText));
    
    if (tariff) {
      // Mostrar preview de la tarifa parseada
      setParsedTariff(tariff);
      // Configurar estado de IVA según lo detectado por OpenAI
      setParsedTariffIncludesVAT(tariff.prices_include_vat);
    }
  }, [tariffText, executeTariffOp, columns]);

  // Confirmar y añadir tarifa a comparación
  const handleConfirmTariff = useCallback(() => {
    if (!parsedTariff) return;

    // Crear tarifa ajustada según el toggle IVA del usuario
    const adjustedTariff: Tariff = {
      ...parsedTariff,
      prices_include_vat: parsedTariffIncludesVAT,
      power: {
        p1_eur_kw_day: parsedTariff.power.p1_eur_kw_day != null
          ? adjustPriceForVAT(parsedTariff.power.p1_eur_kw_day, parsedTariff.prices_include_vat, parsedTariffIncludesVAT)
          : null,
        p2_eur_kw_day: parsedTariff.power.p2_eur_kw_day != null
          ? adjustPriceForVAT(parsedTariff.power.p2_eur_kw_day, parsedTariff.prices_include_vat, parsedTariffIncludesVAT)
          : null,
        p3_eur_kw_day: parsedTariff.power.p3_eur_kw_day != null
          ? adjustPriceForVAT(parsedTariff.power.p3_eur_kw_day, parsedTariff.prices_include_vat, parsedTariffIncludesVAT)
          : null,
      },
      energy: {
        p1_eur_kwh: parsedTariff.energy.p1_eur_kwh != null
          ? adjustPriceForVAT(parsedTariff.energy.p1_eur_kwh, parsedTariff.prices_include_vat, parsedTariffIncludesVAT)
          : null,
        p2_eur_kwh: parsedTariff.energy.p2_eur_kwh != null
          ? adjustPriceForVAT(parsedTariff.energy.p2_eur_kwh, parsedTariff.prices_include_vat, parsedTariffIncludesVAT)
          : null,
        p3_eur_kwh: parsedTariff.energy.p3_eur_kwh != null
          ? adjustPriceForVAT(parsedTariff.energy.p3_eur_kwh, parsedTariff.prices_include_vat, parsedTariffIncludesVAT)
          : null,
      }
    };

    const invoiceColumn = columns.find(col => col.type === 'invoice');
    
    if (invoiceColumn) {
      // Simular coste con la factura existente
      const simulation = simulateTariff(invoiceColumn.data as Invoice, adjustedTariff);
      
      if (simulation) {
        setColumns(prev => [
          ...prev,
          { 
            type: 'tariff', 
            data: adjustedTariff, 
            simulation 
          }
        ]);
      }
    } else {
      // Sin factura, solo añadir la tarifa
      setColumns(prev => [
        ...prev,
        { type: 'tariff', data: adjustedTariff }
      ]);
    }
    
    // Limpiar estados
    setTariffText('');
    setParsedTariff(null);
    setParsedTariffIncludesVAT(true);
  }, [parsedTariff, parsedTariffIncludesVAT, columns]);

  // Cancelar preview de tarifa
  const handleCancelTariff = useCallback(() => {
    setParsedTariff(null);
    setParsedTariffIncludesVAT(true);
  }, []);

  // Eliminar columna de tarifa
  const handleDeleteColumn = useCallback((index: number) => {
    setColumns(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Manejar drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const pdfFile = files.find(file => file.type === 'application/pdf');
    
    if (pdfFile) {
      handleFileUpload(pdfFile);
    }
  }, [handleFileUpload]);

  return (
    <div className="min-h-screen bg-apple-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-apple-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-apple-gray-800">
                PVPC Ingestor + Comparador de Tarifas
              </h1>
              <p className="text-apple-gray-500">
                Pipeline de datos PVPC + análisis inteligente de facturas eléctricas
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Upload de Factura */}
          <div className="bg-white rounded-xl border border-apple-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-apple-gray-800">
                1. Subir Factura PDF
              </h2>
            </div>
            
            {/* Drag & Drop Area */}
            <div
              className="border-2 border-dashed border-apple-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload className="w-12 h-12 text-apple-gray-400 mx-auto mb-4" />
              <p className="text-apple-gray-600 mb-2">
                Arrastra tu factura PDF aquí o haz clic para seleccionar
              </p>
              <p className="text-sm text-apple-gray-400">
                Se extraerá automáticamente el texto para análisis
              </p>
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
              />
            </div>

            {/* Área de texto para factura */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-apple-gray-700 mb-2">
                O pega el texto de tu factura:
              </label>
              <textarea
                value={invoiceText}
                onChange={(e) => setInvoiceText(e.target.value)}
                placeholder="Pega aquí el texto de tu factura eléctrica..."
                className="w-full h-32 p-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
              
              {invoiceError && (
                <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{invoiceError}</span>
                </div>
              )}
              
              <button
                onClick={handleParseInvoice}
                disabled={!invoiceText.trim() || invoiceLoading}
                className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-apple-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {invoiceLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Analizando Factura...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    <span>Analizar Factura</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Añadir Tarifa */}
          <div className="bg-white rounded-xl border border-apple-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Plus className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-apple-gray-800">
                2. Añadir Tarifa para Comparar
              </h2>
            </div>
            
            {!parsedTariff && (
              <div>
                {/* Verificar si hay factura base */}
                {!columns.some(col => col.type === 'invoice') && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span><strong>Primero sube una factura PDF</strong> en el paso 1 para poder comparar tarifas</span>
                    </div>
                  </div>
                )}
                
                <label className="block text-sm font-medium text-apple-gray-700 mb-2">
                  Pega el texto de la tarifa desde la web de la comercializadora:
                </label>
                <textarea
                  value={tariffText}
                  onChange={(e) => setTariffText(e.target.value)}
                  placeholder={`Ejemplo: "Tarifa Plana X: P1 0,120 €/kWh, P2 0,110 €/kWh, P3 0,090 €/kWh, potencia 0,127 €/kW·día..." (luego podrás revisar los datos antes de añadir)`}
                  className="w-full h-32 p-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  disabled={!columns.some(col => col.type === 'invoice')}
                />
                
                {tariffError && (
                  <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{tariffError}</span>
                  </div>
                )}
                
                <button
                  onClick={handleParseTariff}
                  disabled={!tariffText.trim() || tariffLoading || !columns.some(col => col.type === 'invoice')}
                  className="mt-3 w-full bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 disabled:bg-apple-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {tariffLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Analizando Tarifa...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Parsear Tarifa</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Preview de Tarifa Parseada */}
            {parsedTariff && (
              <div className="mt-6 p-4 border border-green-200 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-800">Tarifa Parseada Exitosamente</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Información de la Tarifa */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-apple-gray-800 text-sm">📋 Información</h4>
                    <div className="bg-white p-3 rounded border">
                      <p className="text-sm"><span className="font-medium">Nombre:</span> {parsedTariff.name}</p>
                    </div>
                  </div>

                  {/* Precios de Potencia */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-apple-gray-800 text-sm">⚡ Potencia</h4>
                    <div className="bg-white p-3 rounded border">
                      <p className="text-sm">P1: <span className="font-mono font-medium">{formatPriceForPreview(parsedTariff, 'p1', 'power')}</span></p>
                      <p className="text-sm">P2: <span className="font-mono font-medium">{formatPriceForPreview(parsedTariff, 'p2', 'power')}</span></p>
                      <p className="text-sm">P3: <span className="font-mono font-medium">{formatPriceForPreview(parsedTariff, 'p3', 'power')}</span></p>
                    </div>
                  </div>

                  {/* Precios de Energía */}
                  <div className="space-y-2 md:col-span-2">
                    <h4 className="font-medium text-apple-gray-800 text-sm">🔌 Energía</h4>
                    <div className="bg-white p-3 rounded border">
                      {/* Detectar si es tarifa de precio fijo (solo P1) */}
                      {!parsedTariff.energy.p2_eur_kwh && !parsedTariff.energy.p3_eur_kwh ? (
                        <div className="text-center py-2">
                          <p className="text-xs text-apple-gray-500 mb-1">💡 Tarifa Precio Fijo (24h)</p>
                          <p className="font-mono font-medium text-lg text-green-600">{parsedTariff.energy.p1_eur_kwh?.toFixed(6)} €/kWh</p>
                          <p className="text-xs text-apple-gray-400 mt-1">Sin discriminación horaria</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-apple-gray-500 mb-1">P1 (Punta)</p>
                            <p className="font-mono font-medium">{formatPriceForPreview(parsedTariff, 'p1', 'energy')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-apple-gray-500 mb-1">P2 (Llano)</p>
                            <p className="font-mono font-medium">{formatPriceForPreview(parsedTariff, 'p2', 'energy')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-apple-gray-500 mb-1">P3 (Valle)</p>
                            <p className="font-mono font-medium">{formatPriceForPreview(parsedTariff, 'p3', 'energy')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Toggle IVA para la tarifa parseada */}
                <div className="mb-4 p-3 bg-white border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-amber-800 text-sm mb-1">🧾 Información de IVA</h4>
                      <p className="text-xs text-amber-600">
                        {parsedTariff.prices_include_vat 
                          ? "📋 Análisis automático: Precios CON IVA incluido"
                          : "📋 Análisis automático: Precios SIN IVA (*No incluye impuestos)"
                        }
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-apple-gray-700">Estos precios incluyen IVA:</span>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={parsedTariffIncludesVAT}
                          onChange={(e) => setParsedTariffIncludesVAT(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          parsedTariffIncludesVAT ? 'bg-green-600' : 'bg-apple-gray-300'
                        }`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            parsedTariffIncludesVAT ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </div>
                        <span className="ml-2 text-sm font-medium text-apple-gray-700">
                          {parsedTariffIncludesVAT ? 'Sí' : 'No'}
                        </span>
                      </label>
                    </div>
                  </div>
                  {parsedTariff.prices_include_vat !== parsedTariffIncludesVAT && (
                    <div className="mt-2 text-xs text-blue-600">
                      ℹ️ Los precios se ajustarán automáticamente ({parsedTariffIncludesVAT ? '×1.21 para añadir IVA' : '÷1.21 para quitar IVA'})
                    </div>
                  )}
                </div>

                {/* Botones de Acción */}
                <div className="flex gap-3">
                  <button
                    onClick={handleConfirmTariff}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Confirmar y Añadir al Comparador
                  </button>
                  <button
                    onClick={handleCancelTariff}
                    className="px-4 py-2 border border-apple-gray-300 text-apple-gray-700 rounded-lg hover:bg-apple-gray-50"
                  >
                    Cancelar
                  </button>
                </div>
                
                {/* Mensaje de Simulación */}
                {columns.find(col => col.type === 'invoice') && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                    💡 <strong>Se simulará automáticamente</strong> el coste usando los consumos de tu factura original
                  </div>
                )}
              </div>
            )}

            {/* Instrucciones */}
            <div className="mt-4 p-3 bg-apple-gray-50 rounded-lg">
              <p className="text-xs text-apple-gray-600">
                <strong>Tip:</strong> Copia texto desde webs como Endesa, Iberdrola, 
                Naturgy, etc. El sistema extraerá automáticamente los precios de energía y potencia.
              </p>
            </div>
          </div>
        </div>

        {/* Comparador */}
        {columns.length > 0 && (
          <div className="mb-4 flex justify-end">
            <div className="flex items-center gap-3 bg-white border border-apple-gray-200 rounded-lg px-4 py-2">
              <span className="text-sm font-medium text-apple-gray-700">Ver precios:</span>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showWithVAT}
                  onChange={(e) => setShowWithVAT(e.target.checked)}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showWithVAT ? 'bg-green-600' : 'bg-apple-gray-300'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showWithVAT ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
                <span className="ml-2 text-sm font-medium text-apple-gray-700">
                  {showWithVAT ? 'Con IVA' : 'Sin IVA'}
                </span>
              </label>
            </div>
          </div>
        )}
        <CompareGrid columns={columns} onDeleteColumn={handleDeleteColumn} showWithVAT={showWithVAT} />

        {/* Info del Pipeline PVPC */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mt-1">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">
                Pipeline PVPC Automático
              </h3>
              <p className="text-blue-800 text-sm leading-relaxed">
                Este sistema también incluye un <strong>pipeline automático</strong> que ingesta diariamente 
                los precios PVPC desde la API de REE y los almacena en PostgreSQL. Los datos se actualizan 
                cada día a las 20:30 UTC mediante GitHub Actions.
              </p>
              <p className="text-blue-700 text-xs mt-2">
                <strong>Próxima actualización:</strong> Integraremos los datos históricos PVPC 
                para comparar también con los precios reales del mercado regulado.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
